<script setup lang="ts">
import {
    computed, onBeforeMount, onUnmounted, ref,
} from 'vue';
import { useQuasar } from 'quasar';
import { useSchoolStore } from '@/stores/school';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import DeleteSchoolDialog from '@/components/AdminCenter/DeleteSchoolDialog.vue';
import router from '@/router';
import AdminRepository from '@/repositories/AdminRepository';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import { useUserStore } from '@/stores/user';
import type { AvailableParts } from '@/types/Certificate';

const $q = useQuasar();

const userStore = useUserStore();
const schoolStore = useSchoolStore();

if (!schoolStore.school) {
    router.push('/admin/schools');
    throw new DOMException('No school selected');
}

const { school } = schoolStore;

const schoolVisible = computed(() => (school.isVisible ? 'Yes' : 'No'));
const coursesByPart = computed<{ [key in AvailableParts]: FlightSchoolCourse[] }>(() => (
    {
        141: school.courses.filter((course) => course.certificate.part === 141),
        61: school.courses.filter((course) => course.certificate.part === 61),
    }
));

const loading = ref(true);
const adminEmail = ref<string>('');

const loadCourses = async () => {
    if (!school) {
        return;
    }

    const schoolCourses = await FlightSchoolRepository.getSchoolCourses(school);
    schoolStore.setSchoolCourses(school, schoolCourses);
};

onBeforeMount(async () => {
    if (!school) {
        return;
    }

    $q.loading.show();

    await loadCourses();
    const admins = await AdminRepository.getSchoolAdmins(school);
    adminEmail.value = admins.find((admin) => admin.id === school.owner)?.email || '';

    $q.loading.hide();
    loading.value = false;
});

onUnmounted(() => {
    $q.loading.hide();
});

const deleteSchool = async () => {
    loading.value = true;
    await FlightSchoolRepository.deleteSchool(school.id).then(() => {
        $q.notify({
            color: 'green-5',
            textColor: 'white',
            icon: 'check',
            message: 'School deleted successfully',
        });
        schoolStore.removeSchool(school.id);
        router.push({ name: 'schoolDirectory' });
    }).catch(() => {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
            message: 'Something went wrong',
        });
    });
    loading.value = false;
};

const promptDeleteSchool = () => {
    $q.dialog({
        component: DeleteSchoolDialog,
        componentProps: {
            loading: loading.value,
        },
    }).onOk(() => {
        deleteSchool();
    });
};

</script>
<template>
    <div
        v-if="!loading"
        id="school-info"
        class="text-fsum-black"
    >
        <div class="container">
            <div class="row">
                <div class="col-6">
                    <h3>
                        Flight School Name
                    </h3>
                    <p class="q-mt-xs q-mb-md school-name">
                        {{ school.name }}
                    </p>
                </div>
                <div class="col-6 school-link">
                    <router-link :to="{ name: 'schoolDirectory' }">
                        Select Another School
                    </router-link>
                </div>
            </div>
            <h3>
                Primary Administrator Email Address
            </h3>
            <p class="q-mt-xs q-mb-md email">
                {{ adminEmail }}
            </p>
            <div
                v-for="courses, part in coursesByPart"
                :key="part"
            >
                <h3>
                    {{ part }} Courses
                </h3>
                <p
                    v-if="courses.length === 0"
                    class="q-mt-xs q-mb-md"
                >
                    N/A
                </p>
                <ul
                    v-else
                    class="q-mt-xs q-mb-md course-list text-left flex"
                >
                    <li
                        v-for="course in courses"
                        :key="course.certificate.uuid"
                    >
                        {{ course.certificate.title }}
                    </li>
                </ul>
            </div>
            <h3>
                Street
            </h3>
            <p class="q-mt-xs q-mb-md address">
                {{ school.address.line1 }} <br>
                {{ school.address.line2 }}
            </p>
            <h3>
                City
            </h3>
            <p class="q-mt-xs q-mb-md city">
                {{ school.address.city }}
            </p>
            <div class="row">
                <div class="col-6">
                    <h3>
                        State
                    </h3>
                    <p class="q-mt-xs q-mb-md state">
                        {{ school.address.state }}
                    </p>
                </div>
                <div class="col-6">
                    <h3>
                        Country
                    </h3>
                    <p class="q-mt-xs q-mb-md country">
                        {{ school.address.country }}
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <h3>
                        Zip Code
                    </h3>
                    <p class="q-mt-xs q-mb-md postcode">
                        {{ school.address.postcode }}
                    </p>
                </div>
                <div class="col-6">
                    <h3>
                        Phone
                    </h3>
                    <p class="q-mt-xs q-mb-md phone">
                        {{ school.phone }}
                    </p>
                </div>
            </div>
            <h3>
                Is Visible
            </h3>
            <p class="q-mt-xs q-mb-md is-visible">
                {{ schoolVisible }}
            </p>
            <div
                v-if="userStore.isGlobalAdmin"
                class="row q-mt-xl"
            >
                <div class="col-6">
                    <q-btn
                        label="Edit"
                        type="button"
                        :to="{ name: 'editSchool' }"
                        flat
                        class="edit-btn fsum-button-dark-blue float-left"
                        no-caps
                    />
                </div>
                <div class="col-6">
                    <q-btn
                        label="Delete"
                        type="button"
                        class="delete-btn fsum-button-outlined float-right q-mr-md"
                        no-caps
                        @click="promptDeleteSchool"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
#school-info {
    font-size: 16px;
    text-align: center;
}
div.container {
    max-width: 550px;
    border-radius: 4px;
    margin: auto;
    padding: 40px;
    background-color: var(--fsum-white);
}
h3 {
    display: block;
    text-align: left;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    margin: 0;
}
h3 + p {
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
}
div.school-link a {
    text-decoration: none;
    color: var(--fsum-med-blue);
    font-size: 14px;
    float: right;
}
.course-list {
    max-height: 250px;
    flex-direction: column;
    column-gap: 30px;
    max-width: 500px;
    padding-left: 20px;
}
.course-list li {
    max-width: 220px;
}
:deep(.q-tab__label) {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
}
:deep(.q-tab--inactive .q-tab__indicator) {
    background-color: var(--fsum-gray);
    opacity: 100;
}
:deep(.q-tab--active .q-tab__label) {
    color: var(--fsum-dark-blue2);
}
:deep(.q-tab--inactive .q-tab__label) {
    color: var(--fsum-gray);
}
.q-btn {
    font-size: 16px;
    font-weight: 400;
}
</style>
